import { memo, useEffect, useState } from "react";
import classNames from "classnames";

import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import AIBodyScanDiscover from "@/male/assets/ai-body-scan/ai-body-scan-discover.webp";
import AIBodyScanTrack from "@/male/assets/ai-body-scan/ai-body-scan-track.webp";
import { Question } from "@/types/question";

interface QuestionProps {
    question: Question;
    handleNext(answ: unknown): void;
    isActive: boolean;
}
const ANALYZE_BODY_VIDEO_URL = "https://workbook.zing.coach/web/body-composition-loader.mp4";

const enum AiBodyScanPromoIds {
    Analyze = "analyze",
    Discover = "discover",
    Track = "track",
}

const AI_BODY_SCAN_PROMO_DATA = [
    {
        id: AiBodyScanPromoIds.Analyze,
        title: "Get AI-Powered Insights into Your Body Data",
        event: "man_AIbodyscan_1",
        subTitle:
            "The machine-learning models are trained on industry-standard scanning data and use just your smartphone camera to scan your body!",
        video: ANALYZE_BODY_VIDEO_URL,
    },
    {
        id: AiBodyScanPromoIds.Discover,
        title: "Understand Your Body Like Never Before",
        event: "man_AIbodyscan_2",
        subTitle:
            "Deep-dive into your highly-visual Body Scan Report and see the results integrated into your personalized Zing fitness plan.",
        img: AIBodyScanDiscover,
    },
    {
        id: AiBodyScanPromoIds.Track,
        title: "Always Know Exactly What Your Body Needs",
        event: "man_AIbodyscan_3",
        subTitle:
            "Unlimited Body Scans mean you can track your body data over time for an accurate reflection of your fitness progress with Zing!",
        img: AIBodyScanTrack,
    },
];

const AIBodyScanBodyComponent = memo(
    ({ id, title, subTitle, img, video }: (typeof AI_BODY_SCAN_PROMO_DATA)[0]) => (
        <div
            key={id}
            className="grid h-full w-full flex-none basis-auto content-between justify-items-center gap-10"
        >
            <div className="grid gap-4">
                <h1 className="whitespace-pre-wrap text-center text-zing-24-32 font-semibold">
                    {title}
                </h1>
                <span className="text-center text-zing-16-22 text-zing-neutral-cool-grey-400">
                    {subTitle}
                </span>
            </div>
            <div className="zing-border-gradient-white relative grid w-full justify-center p-6 before:absolute before:inset-0 before:rounded-3xl before:p-[1px] before:content-['']">
                {video && (
                    <video className="h-[315px]" autoPlay muted playsInline loop>
                        <source src={video} type="video/mp4"></source>
                    </video>
                )}
                {img && <img src={img} alt="ai-body-scan-img" className="h-72" />}
            </div>
        </div>
    )
);

const AIBodyScanPromo = ({ question, isActive, handleNext }: QuestionProps) => {
    const [step, setStep] = useState<AiBodyScanPromoIds>(AiBodyScanPromoIds.Analyze);
    const [isDisabledButton, setIsDisabledButton] = useState(false);

    const goToNextQuestion = () => {
        handleNext(null);
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
        });
    };

    useEffect(() => {
        if (isActive) {
            setIsDisabledButton(false);
        }
    }, [isActive]);

    useEffect(() => {
        const currentStep = AI_BODY_SCAN_PROMO_DATA.find(({ id }) => id === step);
        if (isActive && currentStep) {
            logAnalyticsEvent(WebEventTypes.VIEW_EVENT, {
                [WebEventProps.SCREEN]: currentStep.event,
            });
        }
    }, [step, isActive]);

    const handleContinue = () => {
        if (step === AiBodyScanPromoIds.Analyze) {
            setStep(AiBodyScanPromoIds.Discover);
        } else if (step === AiBodyScanPromoIds.Discover) {
            setStep(AiBodyScanPromoIds.Track);
        } else if (step === AiBodyScanPromoIds.Track) {
            setIsDisabledButton(true);

            goToNextQuestion();
        }
    };

    return (
        <div className="flex h-full flex-col justify-between gap-6 pt-2 text-white">
            <div className="overflow-hidden">
                <div
                    className={classNames(
                        "relative flex h-full flex-nowrap transition-transform duration-500",
                        {
                            "translate-x-0": step === AiBodyScanPromoIds.Analyze,
                            "-translate-x-full": step === AiBodyScanPromoIds.Discover,
                            "-translate-x-[200%]": step === AiBodyScanPromoIds.Track,
                        }
                    )}
                >
                    {AI_BODY_SCAN_PROMO_DATA.map((data) => (
                        <AIBodyScanBodyComponent key={data.id} {...data} />
                    ))}
                </div>
            </div>

            <StickyButton isDisabled={isDisabledButton} onClick={handleContinue}>
                Next
            </StickyButton>
        </div>
    );
};

export default memo(AIBodyScanPromo);
